import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';

import { DataManager, Query, Predicate, ReturnOption, WebApiAdaptor, CrudOptions, DataOptions } from '@syncfusion/ej2-data';
import { Fetch } from '@syncfusion/ej2-base';
import { DataResult } from '@syncfusion/ej2-angular-grids';

import { CerAppStateService, DataTableCache } from '../../cer-app/cer-app-state/cer-app-state.service';
import { CerPromptService } from '@ui/cer-ui-prompt.service';
import { DataSourceType, FieldMetadata, FieldMetadataUtil, FieldMetaForeignKey, KeyValue, ViewMetadata, ViewMetadataUtil } from './cer-data-types';
import { clientVersion } from 'src/environments/client-version';

export const headerCerValidationErrorsRespondHttp200: string = 'x-cer-validation-errors-respond-http-200';
export const headerCerAppVersion: string = 'x-cer-app-version';

export class CerWebApiHeaders {
  public static setRequest(request: Request, errorsRespond200: boolean) {
    request.headers.set('Accept', 'application/json, text/javascript, */*; q=0.01');
    if (clientVersion != '1.0.0.0') {
      request.headers.set(headerCerAppVersion, clientVersion);
    }
    if (errorsRespond200) {
      request.headers.set(headerCerValidationErrorsRespondHttp200, 'true');
      request.headers.set('ValidationErrorsRespond200', 'true');
    }
  }
}

export class CerWebApiAdaptor extends WebApiAdaptor {

  override beforeSend(dm: DataManager, request: Request, settings: Fetch): void {
    CerWebApiHeaders.setRequest(request, request.method != 'GET');
  }

  override processResponse(data: DataResult, ds?: DataOptions, query?: Query, xhr?: Request, request?: Fetch, changes?: CrudOptions): Object {
    // console.log('processResponse', data);
    if ((data as any).errors != null) {
      console.log("error" + data);
    }
    return super.processResponse(data, ds, query, xhr, request, changes);
  }
}

@Injectable()
export class CerDataService implements OnDestroy {
  // Subscriptions
  private subscriptionManager: Subscription = new Subscription();

  // Metadata
  public viewMetadata: ViewMetadata;
  public fieldMetadata: FieldMetadata[];

  // Foriegn key data load
  private fkDataTableCountPending: number = 0;
  private fkDataQueued: boolean = false;
  public fkDataLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // Authentication
  //public authToken: string;

  // Data
  public dataSourceType: DataSourceType = 'custom';
  public webApiClient: any;
  public dataManager: DataManager;
  public query: Query;
  public dataManagerUrl: string = '/api/data';
  public data$: BehaviorSubject<Object[]> = new BehaviorSubject<Object[]>([]);
  public parentKeyValue: number = null;

  // Debug
  private debugMode: boolean = false;

  constructor(private stateService: CerAppStateService, private ui: CerPromptService) {
  }

  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }

  public setupWebApiClient(client: object) {
    this.dataSourceType = 'webapi';
    this.webApiClient = client;
  }

  public isOffline(dataSource: any): boolean {
    if (dataSource) {
      const dm = dataSource as DataManager;
      return dm?.dataSource?.offline === true;
    }
    return false;
  }

  public dataSourceLocalGet(data?: any[]): DataManager {
    data = data ?? [];
    var dm = new DataManager(data);
    dm.dataSource.offline = true;
    return dm;
  }

  public initFromMeta(viewMetadata: ViewMetadata, fieldMetadata: FieldMetadata[]) {
    if (viewMetadata != null) {
      this.viewMetadata = viewMetadata;
    }
    if (fieldMetadata != null) {
      this.fieldMetadata = fieldMetadata;
      // Check if need to await foriegn key data
      fieldMetadata.filter(f => FieldMetaForeignKey.dataIsStatic(f)).forEach(f => this.fkDataTableCountPending += 1);
    }

    this.dataManagerSetup();
  }

  public setupPost() {
    this.fkDataQueued = true;
    this.triggerFKDataLoaded();
  }

  //public fkDataPending() {
  //  this.fkDataCountPending++;
  //}

  public onFKTableLoaded() {
    this.fkDataTableCountPending--;
    this.triggerFKDataLoaded();
  }

  private triggerFKDataLoaded() {
    if (this.fkDataQueued && this.fkDataTableCountPending == 0) {
      this.fkDataLoaded$.next(true);
    }
  }

  public fkDataManagerSetup(fieldMetadata: FieldMetadata): DataManager {
    var dataManager: DataManager = null;
    // true: Small static tables are fetched locally.
    // false: For larger dynamic datasets the datamanager queries remote data as needed.

    if (fieldMetadata && fieldMetadata.foreignKeyTableName && fieldMetadata.foreignKeyTableName.length > 0) {
      var isStatic: boolean = (fieldMetadata.foreignKey?.dataOptions?.cache != 'dynamic');
      var isCached: boolean = (isStatic) && (fieldMetadata.foreignKey?.dataOptions?.cache != 'nocache');
      var cache: DataTableCache = null;

      var createWebApiQuery: boolean = true;

      if (isCached) {
        // Small static tables are fetched locally (can can be cached (not implemented yet))
        cache = this.stateService.getDataTableCache(fieldMetadata.foreignKeyTableName, fieldMetadata.foreignKeyTableParams);
        if (cache.loaded || cache.pending) {
          createWebApiQuery = false;
          dataManager = new DataManager(cache.data);
          FieldMetaForeignKey.dataSet(fieldMetadata, cache.data);
          this.onFKTableLoaded();
        }
        else {
          cache.pending = true;
        }
      }
      if (createWebApiQuery) {
        dataManager = this.fkDataManagerWebApi(fieldMetadata, cache);
      }
    }
    return dataManager;
  }

  public fkDataManagerWebApi(fieldMetadata: FieldMetadata, cache: DataTableCache): DataManager {
    var dataManager: DataManager = new DataManager({
      url: this.dataManagerUrl + '/' + fieldMetadata.foreignKeyTableName,
      adaptor: new CerWebApiAdaptor()
    });
    if (cache) {
      var dataManagerExec: DataManager = dataManager;
      var fkData: Object[] = !cache.loaded ? cache.data : new Array<Object>();
      fieldMetadata.foreignKey ??= {};
      fieldMetadata.foreignKey.data = fkData;
      dataManager = new DataManager(fkData);

      var query = new Query();
      if (fieldMetadata.foreignKeyTableParams) {
        fieldMetadata.foreignKeyTableParams.forEach(p => {
          var parms: string[] = p.split('=');
          query.addParams(parms[0], parms.length > 1 ? parms[1] : 'true')
        });
      }
      var predicates: Predicate = this.fkFilterPredicates(fieldMetadata);
      if (predicates) {
        query.where(predicates);
      }
      if (fieldMetadata.foreignKeySortFields) {
        query.sortBy(fieldMetadata.foreignKeySortFields);
      }
      dataManagerExec.executeQuery(query).then(
        e => {
          var returnOption: ReturnOption = e;
          if (returnOption && returnOption.result) {
            if ((<any>returnOption.result).errors) {
              this.ui.errorApi({ error: returnOption.result });
              return;
            }

            (<Object[]>returnOption.result).forEach(
              row => fkData.push(row)
            );
            FieldMetaForeignKey.dataSet(fieldMetadata, fkData);
            if (cache) {
              cache.pending = false;
              cache.loaded = true;
            }
          }
          this.onFKTableLoaded();
        })
        .catch(e => {
          this.ui.errorApiAction(e, "Henter data");
        });
    }
    return dataManager;
  }

  public fkQuery(fieldMetadata: FieldMetadata): Query {
    var query: Query = new Query();
    var predicates: Predicate = this.fkFilterPredicates(fieldMetadata);
    if (predicates) {
      query.where(predicates);
    }
    return query;
  }

  public fkFilterPredicates(fieldMetadata: FieldMetadata): Predicate {
    var predicate: Predicate;
    var filterFieldCount = fieldMetadata.foreignKeyFilterFields?.length;
    if (filterFieldCount > 0) {
      var filterValueCount = fieldMetadata.foreignKeyFilterValues?.length;
      if (filterFieldCount == filterValueCount) {
        var filterOperatorCount = fieldMetadata.foreignKeyFilterOperators?.length ?? 0;
        if (filterOperatorCount == 0) {
          fieldMetadata.foreignKeyFilterOperators = new Array(filterFieldCount).fill('equal');
        }
        if (filterOperatorCount == 1 && filterFieldCount != filterOperatorCount) {
          fieldMetadata.foreignKeyFilterOperators = new Array(filterFieldCount).fill(fieldMetadata.foreignKeyFilterOperators[0]);
        }
        for (let i = 0; i < filterFieldCount; i++) {
          var predicateFilter: Predicate = new Predicate(fieldMetadata.foreignKeyFilterFields[i], fieldMetadata.foreignKeyFilterOperators[i], fieldMetadata.foreignKeyFilterValues[i]);
          predicate = (predicate) ? new Predicate(predicate, 'and', predicateFilter) : predicateFilter;
        }
      }
    }
    return predicate;
  }

  public viewTextSingular(genericTextFallback: boolean): string {
    var text: string = genericTextFallback ? "Post" : "";

    if (this.viewMetadata) {
      if (this.viewMetadata.textSingular) {
        text = this.viewMetadata.textSingular;
      }
      else if (this.viewMetadata.text) {
        text = this.viewMetadata.text;
      }
      else {
        text = this.viewMetadata.name;
      }
    }

    return text;
  }

  public viewDataTitle(rowData: any): string {
    return CerDataService.title(this.viewMetadata, rowData);
  }

  public static title(viewMeta: ViewMetadata, rowData: any): string {
    var dataTitle: string = '';

    if (viewMeta) {
      if (viewMeta.titleFields?.length > 0) {
        viewMeta.titleFields.forEach(fieldName => {
          var value = CerDataService.fieldDisplayValue(rowData, fieldName, viewMeta.fieldMetaData);
          if (value) {
            dataTitle += (dataTitle.length > 0 ? ', ' : '') + value;
          }
        });
      }
      else if (viewMeta.primaryKey) {
        if (Array.isArray(viewMeta.primaryKey)) {
          viewMeta.primaryKey.forEach(fieldName => {
            var value = CerDataService.fieldDisplayValue(rowData, fieldName, viewMeta.fieldMetaData);
            if (value) {
              dataTitle += (dataTitle.length > 0 ? ', ' : '') + value;
            }
          });
        }
        else {
          dataTitle = CerDataService.fieldDisplayValue(rowData, <string>viewMeta.primaryKey, viewMeta.fieldMetaData);
        }
      }
    }
    return dataTitle;
  }

  public fieldValueGetDisplayValue(row: any, fieldName: string): string {
    return CerDataService.fieldDisplayValue(row, fieldName, this.fieldMetadata);
  }

  public static fieldDisplayValue(row: any, fieldName: string, fieldMetas: FieldMetadata[]): string {
    var displayValue: any = '';
    if (row && fieldName?.length > 0) {
      var fieldValue: any = row;
      fieldName.split('.').forEach((c: string) => fieldValue = fieldValue != null ? fieldValue[c] : null);
      if (fieldValue) {
        displayValue = fieldValue;
        const fieldMeta = FieldMetadataUtil.fieldByName(fieldName, fieldMetas);
        if (fieldMeta) {
          displayValue = FieldMetaForeignKey.lookupValue(fieldMeta, fieldValue);
        }
        if (!displayValue) {
          displayValue = fieldValue.toString();
        }
        displayValue = FieldMetadataUtil.formatValue(displayValue, fieldMeta);
      }
    }
    return displayValue;
  }

  /*
  protected fieldValueGetString(data: any, fieldName: string): string {
    var value: string = '';
    if (data && fieldName?.length > 0 && data[fieldName]) {
      value = data[fieldName];
      if (value) {
        value = this.fieldValueGetDisplayValue(fieldName, value);
      }
    }
    return value;
  }
  */

  public dataManagerSetup() {
    if (this.viewMetadata && (this.viewMetadata.dataSource == null || this.viewMetadata.dataSource == 'datamanager')) {
      this.dataSourceType = 'datamanager';

      if ((this.viewMetadata.dataApiUrl != null && this.viewMetadata.dataApiUrl.length > 0) ||
        (this.viewMetadata.dataApiName != null && this.viewMetadata.dataApiName.length > 0)) {
        this.dataManagerSetupApi()
      }
      else if (this.viewMetadata.data) {
        this.dataManagerSetupData()
      }
      if (this.viewMetadata.name) {
        //this.query.fromTable = this.viewMetadata.apiName;
        this.query = new Query();

        if (this.viewMetadata.dataApiParams) {
          this.viewMetadata.dataApiParams.forEach(p => {
            if (typeof p === 'string') {
              var parms: string[] = (<string>p).split('=');
              this.query.addParams(parms[0], parms.length > 1 ? parms[1] : 'true')
            }
            else {
              var keyValue: KeyValue = <KeyValue>p;
              this.query.addParams(keyValue.key, keyValue.value);
            }
          });
        }

        if (this.fieldMetadata) {
          this.fieldMetadata.filter(f => (f.filterOperator && f.filterValue !== undefined && f.filterOnClient !== true))
            .forEach(f =>
              this.queryWhereAdd(f.name, f.filterOperator, f.filterValue)
            )
        }
      }
    }
    else {
      this.dataSourceType = this.viewMetadata?.dataSource ?? 'custom';
    }
  }

  protected dataManagerSetupApi() {
    var apiUrl: string = this.viewMetadata.dataApiUrl;
    if (apiUrl == null) {
      apiUrl = this.dataManagerUrl + '/' + this.viewMetadata.dataApiName;
    }
    this.dataManager = new DataManager({
      url: apiUrl,
      adaptor: new CerWebApiAdaptor(),
    });
    //var dmAny : any = this.dataManager;
    //dmAny.beforeSend = function (dm: any, request: any, settings: any) {
    // console.log("BEFORESEND");
    //request.headers.set('Accept', 'application/json, text/javascript, */*; q=0.01');
    //console.log(dm);
    //console.log(request);
    //console.log(settings);
    //};

    if (this.viewMetadata.insertOverride) {
      this.dataManager.insert = this.viewMetadata.insertOverride;
    }
    if (this.fkDataTableCountPending > 0) {
      this.dataManager.dataSource.offline = true;
    }
  }

  private dataManagerSetupData() {
    this.dataManager = new DataManager(this.viewMetadata.data);
  }

  dataManagerSetData(dataSource: Object | DataManager | DataResult, data: Object[]) {
    const dm = (dataSource as DataManager);
    if (dm?.dataSource?.json) {
      dm.dataSource.json = data;
    }
  }

  public linkParentValueRefresh(parentKeyValue: any): boolean {
    var needRefresh = false;
    if (ViewMetadataUtil.linkIsParentLinkDefined(this.viewMetadata)) {
      this.parentKeyValue = parentKeyValue;
      var whereFieldName: string = ViewMetadataUtil.linkFieldNameGet(this.viewMetadata);
      if (whereFieldName.startsWith("$")) {
        this.apiParamAdd(whereFieldName, parentKeyValue);
      }
      else {
        var whereOperator: string = 'equal';
        this.queryWhereAdd(whereFieldName, whereOperator, parentKeyValue);
      }
      this.query = this.query.clone();
      needRefresh = true;
    }
    return needRefresh;
  }

  protected debug(s: string) {
    if (this.debugMode) {
      console.log(s);
    }
  }

  public setDebugMode(active: boolean) {
    if (active || this.debugMode) {

      this.apiParamAdd("$debug", active);
    }
    this.debugMode = active;
  }

  public apiParamAdd(key: string, value: any) {
    if (this.query) {
      if (key !== null && key.length > 0) {
        var found: boolean = false;
        this.query.params.forEach(p => {
          if (p.key == key) {
            p.value = value;
            found = true;
          }
        });
        if (!found) {
          this.query.addParams(key, value);
        }
      }
    }

  }

  public queryWhereAdd(fieldName: string, operator: string, filterValue: any) {
    this.query.queries = this.query.queries
      .filter(p => !this.isQueryField(p, 'onWhere', fieldName, operator));  // Remove existing query predicate
    if (operator == 'in') {
      if (filterValue && filterValue.length > 0) {
        var predicate: Predicate = new Predicate(fieldName, 'equal', filterValue[0]);
        for (var i = 1; i < filterValue.length; i++) {
          predicate = predicate.or(new Predicate(fieldName, 'equal', filterValue[i]));
        }
        this.query.where(predicate);
      }
    }
    else {
      this.query.where(fieldName, operator, filterValue);
    }
  }

  private isQueryField(query: any, fn: any, fieldName: any, operator: any): boolean {
    var found: boolean = false;

    if (query.fn == fn) {
      if (query.fieldName == fieldName) {
        found = true;
      }
      else if (query.e && query.e.field == fieldName && query.e.operator == operator) {
        found = true;
      }
    }
    return found;
  }

  // Future
  private dataGet() {
    switch (this.dataSourceType) {
      case 'webapi':
        if (this.webApiClient) {
          this.webApiClient.get().subscribe((data: any) => this.data$.next(data));
        }
        break;
      case 'datamanager':
        if (this.dataManager) {
          //          this.dataManager.executeQuery(new Query().then(data => this.dataPublish(data));
        }
        break;
      case 'custom':
        break;
    }
  }
}
